import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { getEmpresaClienteByEmail, empresasClientes, type EmpresaCliente } from '@/mocks/admin/empresasClientes';
import { getOperadorByEmail, getOperadoresByEmpresaId, operadoresAdmin, type OperadorAdmin, type OperadorRole } from '@/mocks/admin/operadoresAdmin';

const ADMIN_AUTH_KEY = 'admin_auth';

export type { OperadorRole, OperadorAdmin };

export interface AdminSession {
  isAuthenticated: boolean;
  userId?: string;
  nome?: string;
  email?: string;
  role?: OperadorRole;
  empresaId?: string;
  empresaNome?: string;
  empresaCnpj?: string;
  isAdminEmpresa?: boolean;
  avatar?: string;
  loginAt?: string;
}

interface AdminAuthContextType {
  session: AdminSession;
  isAuthenticated: boolean;
  role: OperadorRole | null;
  empresaId: string | null;
  empresaNome: string | null;
  login: (email: string, senha: string) => { success: boolean; error?: string };
  logout: () => void;
  operadoresDaEmpresa: OperadorAdmin[];
  empresaCliente: EmpresaCliente | undefined;
  adicionarOperador: (nome: string, email: string, senha: string, role: OperadorRole, cpf?: string) => { success: boolean; error?: string };
  removerOperador: (operadorId: string) => { success: boolean; error?: string };
  alterarRoleOperador: (operadorId: string, novoRole: OperadorRole) => { success: boolean; error?: string };
  alternarStatusOperador: (operadorId: string) => { success: boolean; error?: string; ativo?: boolean };
  recarregarOperadores: () => void;
  editarDadosOperador: (operadorId: string, nome?: string, email?: string, cpf?: string, senha?: string) => { success: boolean; error?: string };
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

function getInitialSession(): AdminSession {
  try {
    const stored = localStorage.getItem(ADMIN_AUTH_KEY);
    if (stored) {
      return JSON.parse(stored) as AdminSession;
    }
  } catch {
    // ignore
  }
  return { isAuthenticated: false };
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession>(getInitialSession);
  const [empresaCliente, setEmpresaCliente] = useState<EmpresaCliente | undefined>(() => {
    const s = getInitialSession();
    if (s.isAuthenticated && s.email) {
      return getEmpresaClienteByEmail(s.email);
    }
    return undefined;
  });
  const [operadoresDaEmpresa, setOperadoresDaEmpresa] = useState<OperadorAdmin[]>(() => {
    const s = getInitialSession();
    if (s.isAuthenticated && s.empresaId) {
      return getOperadoresByEmpresaId(s.empresaId);
    }
    return [];
  });

  const login = useCallback((email: string, senha: string): { success: boolean; error?: string } => {
    // Primeiro tenta login como admin da empresa (dono)
    const empresa = getEmpresaClienteByEmail(email);
    if (empresa) {
      if (senha !== empresa.senha) {
        return { success: false, error: 'Senha incorreta para o administrador da empresa' };
      }
      const operadores = getOperadoresByEmpresaId(empresa.id);
      const newSession: AdminSession = {
        isAuthenticated: true,
        userId: `admin-${empresa.id}`,
        nome: `Admin - ${empresa.nome}`,
        email: empresa.emailAdmin,
        role: 'admin_empresa',
        empresaId: empresa.id,
        empresaNome: empresa.nome,
        empresaCnpj: empresa.cnpj,
        isAdminEmpresa: true,
        loginAt: new Date().toISOString(),
      };
      localStorage.setItem(ADMIN_AUTH_KEY, JSON.stringify(newSession));
      setSession(newSession);
      setEmpresaCliente(empresa);
      setOperadoresDaEmpresa(operadores);
      return { success: true };
    }

    // Tenta login como operador
    const operador = getOperadorByEmail(email);
    if (!operador) {
      return { success: false, error: 'Email não encontrado. Verifique suas credenciais.' };
    }
    if (!operador.ativo) {
      return { success: false, error: 'Este usuário está inativo. Entre em contato com o administrador da empresa.' };
    }
    if (senha !== operador.senha) {
      return { success: false, error: 'Senha incorreta' };
    }

    const empresaDoOperador = empresasClientes.find(e => e.id === operador.empresaId);
    const operadores = getOperadoresByEmpresaId(operador.empresaId);
    const newSession: AdminSession = {
      isAuthenticated: true,
      userId: operador.id,
      nome: operador.nome,
      email: operador.email,
      role: operador.role,
      empresaId: operador.empresaId,
      empresaNome: empresaDoOperador?.nome || '',
      empresaCnpj: empresaDoOperador?.cnpj || '',
      isAdminEmpresa: false,
      avatar: operador.avatar,
      loginAt: new Date().toISOString(),
    };
    localStorage.setItem(ADMIN_AUTH_KEY, JSON.stringify(newSession));
    setSession(newSession);
    setEmpresaCliente(empresaDoOperador);
    setOperadoresDaEmpresa(operadores);
    return { success: true };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    setSession({ isAuthenticated: false });
    setEmpresaCliente(undefined);
    setOperadoresDaEmpresa([]);
  }, []);

  const recarregarOperadores = useCallback(() => {
    if (session.empresaId) {
      setOperadoresDaEmpresa([...getOperadoresByEmpresaId(session.empresaId)]);
    }
  }, [session.empresaId]);

  const adicionarOperador = useCallback((nome: string, email: string, senha: string, role: OperadorRole, cpf?: string) => {
    if (!session.empresaId) {
      return { success: false, error: 'Nenhuma empresa na sessão' };
    }
    const novoOperador: OperadorAdmin = {
      id: `op-${session.empresaId}-${Date.now()}`,
      empresaId: session.empresaId,
      nome,
      email,
      cpf,
      senha,
      role,
      ativo: true,
    };
    operadoresAdmin.push(novoOperador);
    recarregarOperadores();
    return { success: true };
  }, [session.empresaId, recarregarOperadores]);

  const removerOperador = useCallback((operadorId: string) => {
    const index = operadoresAdmin.findIndex(o => o.id === operadorId);
    if (index === -1) {
      return { success: false, error: 'Operador não encontrado' };
    }
    operadoresAdmin.splice(index, 1);
    recarregarOperadores();
    return { success: true };
  }, [recarregarOperadores]);

  const alterarRoleOperador = useCallback((operadorId: string, novoRole: OperadorRole) => {
    const operador = operadoresAdmin.find(o => o.id === operadorId);
    if (!operador) {
      return { success: false, error: 'Operador não encontrado' };
    }
    operador.role = novoRole;
    recarregarOperadores();
    return { success: true };
  }, [recarregarOperadores]);

  const alternarStatusOperador = useCallback((operadorId: string) => {
    const operador = operadoresAdmin.find(o => o.id === operadorId);
    if (!operador) {
      return { success: false, error: 'Operador não encontrado' };
    }
    operador.ativo = !operador.ativo;
    recarregarOperadores();
    return { success: true, ativo: operador.ativo };
  }, [recarregarOperadores]);

  const editarDadosOperador = useCallback((operadorId: string, nome?: string, email?: string, cpf?: string, senha?: string) => {
    const operador = operadoresAdmin.find(o => o.id === operadorId);
    if (!operador) {
      return { success: false, error: 'Operador não encontrado' };
    }
    if (nome !== undefined) operador.nome = nome;
    if (email !== undefined) operador.email = email;
    if (cpf !== undefined) operador.cpf = cpf;
    if (senha !== undefined) operador.senha = senha;
    recarregarOperadores();
    return { success: true };
  }, [recarregarOperadores]);

  return (
    <AdminAuthContext.Provider value={{
      session,
      isAuthenticated: session.isAuthenticated === true,
      role: session.role || null,
      empresaId: session.empresaId || null,
      empresaNome: session.empresaNome || null,
      login,
      logout,
      operadoresDaEmpresa,
      empresaCliente,
      adicionarOperador,
      removerOperador,
      alterarRoleOperador,
      alternarStatusOperador,
      recarregarOperadores,
      editarDadosOperador,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextType {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth deve ser usado dentro de um AdminAuthProvider');
  }
  return context;
}

export function getAdminSession(): AdminSession {
  try {
    const stored = localStorage.getItem(ADMIN_AUTH_KEY);
    if (stored) {
      return JSON.parse(stored) as AdminSession;
    }
  } catch {
    // ignore
  }
  return { isAuthenticated: false };
}